/**
 * Simple Photo Upload Component
 * Clean, minimal design that integrates seamlessly into forms
 */

import React, { useState } from 'react';
import { X, Check, AlertCircle, Camera, Upload } from 'lucide-react';
import Image from 'next/image';
import { UploadDropzone } from "@/utils/uploadthing";

interface SimplePhotoUploadProps {
  photoUrl: string | null;
  onPhotoChange: (url: string | null) => void;
  onUploadStart?: () => void;
  onUploadComplete?: () => void;
  onUploadError?: (error: string) => void;
  className?: string;
  /** The consent tick, passed to the uploader (photoConsent on /api/supabase-upload). */
  photoConsent?: string | null;
  /** A file picked while unticked is held until the tick (see utils/uploadthing.tsx). */
  requireConsent?: boolean;
  onConsentRequired?: () => void;
}

export function SimplePhotoUpload({
  photoUrl,
  onPhotoChange,
  onUploadStart,
  onUploadComplete,
  onUploadError,
  className = '',
  photoConsent,
  requireConsent,
  onConsentRequired,
}: SimplePhotoUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleUploadComplete = (res: any) => {
    if (res && res.length > 0) {
      const uploadedUrl = res[0].url;
      onPhotoChange(uploadedUrl);
      onUploadComplete?.();
      setImageError(false);
    }
    setIsUploading(false);
  };

  const handleUploadError = (error: Error) => {
    console.error('Upload error:', error);
    onUploadError?.(error.message);
    setIsUploading(false);
  };

  const handleUploadStart = () => {
    setIsUploading(true);
    setImageError(false);
    onUploadStart?.();
  };

  const handleRemovePhoto = () => {
    onPhotoChange(null);
    setImageError(false);
  };

  if (photoUrl) {
    return (
      <div className={`flex items-start space-x-4 ${className}`}>
        {/* Photo Display */}
        <div className="relative group">
          <div className="w-32 h-40 bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
            {!imageError ? (
              <Image
                src={photoUrl}
                alt="Uploaded photo"
                fill
                className="object-cover"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                <div className="text-center p-2">
                  <AlertCircle className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">Failed to load</p>
                </div>
              </div>
            )}
          </div>
          
          {/* Remove button */}
          <button
            onClick={handleRemovePhoto}
            className="absolute -top-2 -right-2 bg-white border border-gray-200 hover:bg-red-50 hover:border-red-300 text-gray-600 hover:text-red-600 rounded-full p-1.5 shadow-sm transition-all opacity-0 group-hover:opacity-100"
            title="Remove photo"
            type="button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Success info */}
        <div className="flex-1 py-2">
          <div className="flex items-center space-x-2 text-sm mb-2">
            <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-3 h-3 text-green-600" />
            </div>
            <span className="text-green-700 font-medium">Photo uploaded</span>
          </div>
          <button
            onClick={handleRemovePhoto}
            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            type="button"
          >
            Change photo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {isUploading ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <div className="inline-flex items-center space-x-2">
            <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
            <span className="text-gray-600">Uploading...</span>
          </div>
        </div>
      ) : (
        <UploadDropzone
          endpoint="childPhotoUploader"
          photoConsent={photoConsent}
          requireConsent={requireConsent}
          onConsentRequired={onConsentRequired}
          onClientUploadComplete={handleUploadComplete}
          onUploadError={handleUploadError}
          onUploadBegin={handleUploadStart}
          config={{
            mode: "auto"
          }}
          appearance={{
            container: "border-2 border-dashed border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg",
            uploadIcon: "text-gray-400",
            label: "text-gray-600 text-sm font-normal",
            allowedContent: "text-xs text-gray-500 mt-1",
            button: "bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors mt-3"
          }}
          content={{
            uploadIcon: () => <Camera className="w-8 h-8 mb-2 text-gray-400" />,
            label: "Click to upload a photo",
            allowedContent: "JPG, PNG, WebP or iPhone HEIC, any size from your phone",
            button: ({ ready }) => ready ? "Choose Photo" : "Processing..."
          }}
        />
      )}
    </div>
  );
}
